import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { allPages, gridCells, hasOverride, layoutGridSpec, pageInfo, photoPageKey, resolveDesign, snapToGrid, type ElementBox, type PageLayout, type ProjectDoc, type Rect } from '@cal/engine';
import { PageSvg, type Handle } from '../render/PageSvg';
import { edit, setProp, useEditor } from './store';
import { useGeometry, usePageLayout, type useDocResources } from './hooks';
import { Seg, Spinner } from '../ui/controls';
import { AddBlockMenu } from './AddBlockMenu';

export const ASSET_MIME = 'application/x-calendar-asset';

/** Assign a pool photo to an image slot on a page. */
export function assignPhoto(pageKey: string, slot: number, assetId: string) {
  edit((d) => ({ ...d, photos: { ...d.photos, [pageKey]: { ...(d.photos[pageKey] ?? {}), [slot]: { assetId, fit: 'fill', zoom: 1, panX: 0, panY: 0 } } } }), `assign:${pageKey}:${slot}`);
}

export function Canvas({ doc, res, layouts, onToggleInspector }: { doc: ProjectDoc; res: ReturnType<typeof useDocResources>; layouts: Map<string, PageLayout>; onToggleInspector: () => void }) {
  const page = useEditor((s) => s.page);
  const view = useEditor((s) => s.view);
  const zoom = useEditor((s) => s.zoom);
  const guides = useEditor((s) => s.guides);
  const selected = useEditor((s) => s.selected);
  const stage = useEditor((s) => s.stage);
  const geometry = useGeometry(doc);
  const layout = usePageLayout(doc, page, res);
  const wrap = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 800, h: 600 });
  useLayoutEffect(() => {
    if (!wrap.current) return;
    const ro = new ResizeObserver(([e]) => setBox({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  const pages = allPages(doc);
  const idx = pages.findIndex((p) => p.key === page);
  const info = pageInfo(doc, page);
  const totalW = layout.trimW + 2 * layout.bleed, totalH = layout.trimH + 2 * layout.bleed;
  const fitPxPerMm = Math.max(0.5, Math.min((box.w - 48) / totalW, (box.h - 48) / totalH));
  const pxPerMm = zoom === 'fit' ? fitPxPerMm : zoom;
  const overridden = new Set(resolveDesign(doc, page).elements.filter((e) => hasOverride(doc, page, e.id)).map((e) => e.id));

  // drag to move, or drag a handle to resize, the selected element — snapping to the layout grid unless Alt is held
  const spec = layoutGridSpec(doc, page);
  const drag = useRef<{ id: string; mode: 'move' | Handle; x0: number; y0: number; rect: ElementBox['rect']; raf: number } | null>(null);
  const onBoxPointerDown = (b: ElementBox, e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { id: b.id, mode: 'move', x0: e.clientX, y0: e.clientY, rect: b.rect, raf: 0 };
  };
  const onHandlePointerDown = (b: ElementBox, h: Handle, e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { id: b.id, mode: h, x0: e.clientX, y0: e.clientY, rect: b.rect, raf: 0 };
  };
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      cancelAnimationFrame(d.raf);
      d.raf = requestAnimationFrame(() => {
        const dx = (e.clientX - d.x0) / pxPerMm, dy = (e.clientY - d.y0) / pxPerMm;
        if (Math.abs(dx) < 0.3 && Math.abs(dy) < 0.3) return;
        const r = d.rect;
        let next: Rect;
        if (d.mode === 'move') next = { ...r, x: r.x + dx, y: r.y + dy };
        else {
          let { x, y, w, h } = r;
          if (d.mode.includes('w')) { x = Math.min(r.x + r.w - 1, r.x + dx); w = r.x + r.w - x; }
          if (d.mode.includes('e')) w = Math.max(1, r.w + dx);
          if (d.mode.includes('n')) { y = Math.min(r.y + r.h - 1, r.y + dy); h = r.y + r.h - y; }
          if (d.mode.includes('s')) h = Math.max(1, r.h + dy);
          next = { x, y, w, h };
        }
        const snapped = doc.layoutGrid.snap && !e.altKey ? snapToGrid(next, spec, d.mode === 'move' ? 'move' : 'resize') : next;
        const out = { x: +snapped.x.toFixed(1), y: +snapped.y.toFixed(1), w: +snapped.w.toFixed(1), h: +snapped.h.toFixed(1) };
        setProp(d.id, ['rect'], out, `drag:${d.id}:${d.x0}`);
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [pxPerMm, doc.layoutGrid.snap, spec.area.x, spec.area.y, spec.cellW, spec.cellH, spec.gutter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = (e: React.DragEvent) => {
    const assetId = e.dataTransfer.getData(ASSET_MIME);
    if (!assetId) return;
    e.preventDefault();
    const target = (e.target as Element).closest('[data-el]');
    const elId = target?.getAttribute('data-el');
    const design = resolveDesign(doc, page);
    const el = design.elements.find((x) => x.id === elId && x.type === 'image') ?? design.elements.find((x) => x.type === 'image');
    if (el && el.type === 'image') assignPhoto(photoPageKey(page, el), el.slot, assetId);
  };

  const loading = res.fontsLoading || res.assetsLoading;
  return (
    <>
      <div className="canvas-bar" role="toolbar" aria-label="Preview controls">
        <Seg label="View" value={view} onChange={(v) => useEditor.setState({ view: v })} options={[{ value: 'single', label: 'Single page' }, { value: 'overview', label: doc.product.productType === 'monthly' ? 'All months' : 'All pages' }]} />
        {view === 'single' && (
          <>
            {stage === 2 && <AddBlockMenu doc={doc} />}
            <button className="btn sm icon" aria-label="Previous page" title="Previous page ([ or PageUp)" disabled={idx <= 0} onClick={() => useEditor.setState({ page: pages[idx - 1].key })}>‹</button>
            <strong style={{ minWidth: 120, textAlign: 'center' }}>{info.label} {info.month ? doc.year : ''}</strong>
            <button className="btn sm icon" aria-label="Next page" title="Next page (] or PageDown)" disabled={idx >= pages.length - 1} onClick={() => useEditor.setState({ page: pages[idx + 1].key })}>›</button>
            <span className="spacer" />
            <button className="btn sm icon" aria-label="Zoom out" onClick={() => useEditor.setState({ zoom: Math.max(0.5, pxPerMm / 1.25) })}>−</button>
            <span className="small mono" style={{ width: 44, textAlign: 'center' }} aria-live="polite">{Math.round((pxPerMm / 3.7795) * 100)}%</span>
            <button className="btn sm icon" aria-label="Zoom in" onClick={() => useEditor.setState({ zoom: Math.min(30, pxPerMm * 1.25) })}>+</button>
            <button className={`btn sm ${zoom === 'fit' ? 'active' : ''}`} title="Fit page to window (0)" onClick={() => useEditor.setState({ zoom: 'fit' })}>Fit</button>
            <button className="btn sm" onClick={() => useEditor.setState({ zoom: 3.7795 })} title="Actual size on a 96 dpi screen">100%</button>
          </>
        )}
        {view === 'overview' && <span className="spacer" />}
        <span className="row small" style={{ gap: 10 }} role="group" aria-label="Guides">
          <label className="check"><input type="checkbox" checked={guides.bleed} onChange={(e) => useEditor.setState({ guides: { ...guides, bleed: e.target.checked } })} />Trim/bleed</label>
          <label className="check"><input type="checkbox" checked={guides.safe} onChange={(e) => useEditor.setState({ guides: { ...guides, safe: e.target.checked } })} />Safe area</label>
          <label className="check" title="Area kept clear for binding / hanging"><input type="checkbox" checked={guides.binding} onChange={(e) => useEditor.setState({ guides: { ...guides, binding: e.target.checked } })} />Clearance</label>
          {stage === 2 && <>
          <label className="check" title="Show the layout grid blocks snap to"><input type="checkbox" checked={doc.layoutGrid.show} onChange={(e) => edit((d) => ({ ...d, layoutGrid: { ...d.layoutGrid, show: e.target.checked } }), 'grid-show')} />Show grid</label>
          <label className="check" title="Snap blocks to whole grid cells (hold Alt to place freely)"><input type="checkbox" checked={doc.layoutGrid.snap} onChange={(e) => edit((d) => ({ ...d, layoutGrid: { ...d.layoutGrid, snap: e.target.checked } }), 'grid-snap')} />Snap to grid</label>
          </>}
        </span>
        <button className="btn sm inspector-toggle" onClick={onToggleInspector} style={{ display: box.w + 640 < 1100 ? undefined : 'none' }}>Element settings</button>
        {loading && <Spinner label="Loading fonts & images" />}
      </div>
      <div className="canvas-wrap" ref={wrap} onDragOver={(e) => e.dataTransfer.types.includes(ASSET_MIME) && e.preventDefault()} onDrop={onDrop}>
        {view === 'single' ? (
          <div className="canvas-inner">
            <PageSvg layout={layout} geometry={geometry(page)} widthPx={totalW * pxPerMm} quality="preview" guides={guides} selectedId={selected} overridden={overridden}
              onSelect={(id) => useEditor.setState({ selected: id })} onBoxPointerDown={onBoxPointerDown} onHandlePointerDown={onHandlePointerDown}
              gridCells={doc.layoutGrid.show ? gridCells(spec) : undefined} fontVersion={res.fontVersion}
              ariaLabel={`${info.label} page preview`} />
          </div>
        ) : (
          <div className="overview">
            {pages.map((p) => {
              const l = layouts.get(p.key);
              const errs = l?.issues.filter((i) => i.severity === 'error').length ?? 0;
              return (
                <button key={p.key} className="overview-item" onClick={() => useEditor.setState({ page: p.key, view: 'single' })} aria-label={`Open ${p.label}`}>
                  <span className="lbl">{p.label}{hasOverride(doc, p.key) && <span className="override-dot" title="Has monthly changes" />}{errs > 0 && <span className="badge err">{errs} issue{errs > 1 ? 's' : ''}</span>}</span>
                  {l && <PageSvg layout={l} geometry={geometry(p.key)} widthPx={Math.min(320, Math.max(180, (box.w - 80) / 4))} quality="thumb" showBleed={false} fontVersion={res.fontVersion} ariaLabel={`${p.label} thumbnail`} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
